<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    :max-width="$vuetify.breakpoint.smAndDown ? 'none' : '560px'"
    :transition="$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    persistent
    scrollable
  >
    <v-card>
      <!-- Mobile toolbar -->
      <v-toolbar flat light color="white" v-if="$vuetify.breakpoint.smAndDown">
        <v-toolbar-title>{{ $t('messenger.newConversation') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn light icon @click="close">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider v-if="$vuetify.breakpoint.smAndDown"></v-divider>

      <!-- Desktop close -->
      <v-container class="pb-0" v-if="$vuetify.breakpoint.mdAndUp">
        <v-row justify="end">
          <v-col class="shrink">
            <v-btn color="color-grey" depressed right text small @click="close">
              <v-icon class="mr-3">fa-times</v-icon>
              <span>{{ $t('close') }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>

      <!-- Step 1: Topic -->
      <v-card-text v-if="step === 1" class="pa-0">
        <v-container fluid class="pt-0">
          <v-row justify="center">
            <v-col class="shrink text-center">
              <v-icon color="color-new-link-blue" :size="$vuetify.breakpoint.smAndDown ? 60 : 76">far fa-comments</v-icon>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="text-center">
              <h3 class="mt-2 text-h3">{{ $t('messenger.newConversation') }}</h3>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" sm="10" class="text-center">
              <p class="font-weight-bold mb-1">{{ $t('messenger.selectTopic') }}</p>
              <p class="mb-4">{{ $t('messenger.selectTopicDescription') }}</p>
            </v-col>
          </v-row>

          <v-row justify="center" v-if="topicsLoading">
            <v-col class="text-center py-6">
              <v-progress-circular indeterminate color="primary" :size="36" :width="3" />
            </v-col>
          </v-row>
          <v-row justify="center" v-else-if="topicsError">
            <v-col class="text-center py-4">
              <v-icon color="error" class="mb-2">fas fa-exclamation-circle</v-icon>
              <p class="text-body-2 mb-0">{{ $t('messenger.topicsError') }}</p>
            </v-col>
          </v-row>
          <v-row justify="center" v-else>
            <v-col cols="12" sm="10" class="d-flex justify-center">
              <div class="topic-list__inner">
                <div
                  v-for="topic in topics"
                  :key="topic.nr"
                  :class="['topic-option', { 'topic-option--selected': selectedTopicNr === topic.nr }]"
                  @click="selectedTopicNr = topic.nr"
                >
                  <v-icon v-if="selectedTopicNr === topic.nr" color="primary" class="topic-option__check">
                    fas fa-check-circle
                  </v-icon>
                  <v-icon v-else color="#C5C9D0" class="topic-option__check">
                    far fa-circle
                  </v-icon>
                  <span :class="['topic-option__label', { 'font-weight-bold': selectedTopicNr === topic.nr }]">
                    {{ topicLabel(topic) }}
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider v-if="step === 1"></v-divider>
      <v-card-actions v-if="step === 1" class="justify-center pa-4">
        <v-btn tile
               large
               :class="['primary', { 'wide-button': $vuetify.breakpoint.mdAndUp }]"
               :disabled="selectedTopicNr === null || topicsLoading || topicsError"
               @click="next">
          {{ $t('messenger.continue') }}
        </v-btn>
      </v-card-actions>

      <!-- Step 2: Contact information -->
      <v-card-text v-if="step === 2" class="pa-0">
        <v-container fluid class="pt-0">
          <v-row justify="center">
            <v-col class="shrink text-center">
              <v-icon color="color-new-link-blue" :size="$vuetify.breakpoint.smAndDown ? 60 : 76">far fa-comments</v-icon>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="text-center">
              <h3 class="mt-2 text-h3">{{ $t('messenger.confirmContactHeader') }}</h3>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" sm="10">
              <p class="text-center">{{ $t('messenger.confirmContactDesc1') }}</p>
              <p class="text-center">{{ $t('messenger.confirmContactDesc2') }}</p>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" sm="10">
              <p class="ok-title bold">{{ $t('messenger.email') }}</p>
              <v-text-field
                v-model="contactEmail"
                outlined
                dense
                :error-messages="emailErrors"
                @blur="$v.contactEmail.$touch()"
                @input="$v.contactEmail.$touch()"
              ></v-text-field>

              <p class="ok-title bold">{{ $t('messenger.phone') }}</p>
              <v-text-field
                v-model="contactPhoneNumber"
                outlined
                dense
                prefix="+358"
                class="long-prefix"
                :error-messages="phoneErrors"
                @blur="$v.contactPhoneNumber.$touch()"
                @input="$v.contactPhoneNumber.$touch()"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" sm="10" class="d-flex justify-center">
              <v-checkbox
                v-model="contactConfirmed"
                :label="$t('messenger.contactConfirmed')"
                color="primary"
                class="mt-2"
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider v-if="step === 2"></v-divider>
      <v-card-actions v-if="step === 2" class="justify-space-between pa-4">
        <v-btn tile large outlined color="color-new-link-blue" :class="{ 'wide-button': $vuetify.breakpoint.mdAndUp }" @click="back">{{ $t('back') }}</v-btn>
        <v-btn
          tile
          large
          :class="['primary', { 'wide-button': $vuetify.breakpoint.mdAndUp }]"
          :disabled="!contactConfirmed || $v.contactEmail.$invalid || $v.contactPhoneNumber.$invalid || savingContact"
          :loading="savingContact"
          @click="next"
        >
          {{ $t('messenger.continue') }}
        </v-btn>
      </v-card-actions>

      <!-- Step 3: Debts + message -->
      <v-card-text v-if="step === 3" class="pa-0">
        <v-container fluid class="pt-0">
          <v-row justify="center">
            <v-col class="shrink text-center">
              <v-icon color="color-new-link-blue" :size="$vuetify.breakpoint.smAndDown ? 60 : 76">far fa-comments</v-icon>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="text-center">
              <h3 class="mt-2 text-h3">{{ $t('messenger.tellUsMore') }}</h3>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" sm="10">
              <p class="ok-title bold">{{ $t('messenger.openingMessage') }} *</p>
              <v-textarea
                v-model="openingMessage"
                outlined
                dense
                rows="4"
                :error-messages="messageError"
                @blur="$v.openingMessage.$touch()"
                @input="$v.openingMessage.$touch()"
                counter
                maxlength="4096"
              ></v-textarea>

              <p class="ok-title bold">{{ $t('messenger.selectDebts') }} *</p>
              <v-select
                v-model="selectedDebtNrs"
                :items="formattedDebts"
                color="color-new-link-blue"
                outlined
                multiple
                chips
                small-chips
                :error-messages="debtsError"
                @blur="$v.selectedDebtNrs.$touch()"
                @change="$v.selectedDebtNrs.$touch()"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip small v-if="index < 2">{{ item.text }}</v-chip>
                  <span v-if="index === 2" class="grey--text text-caption ml-1">+{{ selectedDebtNrs.length - 2 }}</span>
                </template>
              </v-select>
              <p class="text-body-2 mt-n3 mb-4 color-new-body-grey--text">{{ $t('messenger.selectDebtsHint') }}</p>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider v-if="step === 3"></v-divider>
      <v-card-actions v-if="step === 3" class="justify-space-between pa-4">
        <v-btn tile large outlined color="color-new-link-blue" :class="{ 'wide-button': $vuetify.breakpoint.mdAndUp }" @click="back">{{ $t('back') }}</v-btn>
        <v-btn
          tile
          large
          :class="['primary', { 'wide-button': $vuetify.breakpoint.mdAndUp }]"
          :disabled="!canSend"
          :loading="starting"
          @click="send"
        >
          {{ $t('messenger.startConversation') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { apiResource } from '@/util/resources'
  import { required, numeric, email, helpers, minLength } from 'vuelidate/lib/validators'

  const strictEmail = helpers.regex('strictEmail', /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)
  const validFinnishPhone = helpers.regex('validFinnishPhone', /^[2-9]\d{5,9}$/)

  export default {
    name: 'NewConversationDialog',

    props: {
      value: {
        type: Boolean,
        default: false
      }
    },

    data: function () {
      return {
        step: 1,
        topics: [],
        topicsLoading: false,
        topicsError: false,
        selectedTopicNr: null,
        contactEmail: '',
        contactPhoneNumber: '',
        originalContactEmail: '',
        originalContactPhoneNumber: '',
        contactConfirmed: false,
        selectedDebtNrs: [],
        openingMessage: '',
        starting: false,
        savingContact: false,
        contactAllowPosted: false
      }
    },

    validations: {
      contactEmail: {
        required: required,
        email: email,
        strictEmail: strictEmail
      },
      contactPhoneNumber: {
        numeric: numeric,
        minLength: minLength(6),
        validFinnishPhone: validFinnishPhone
      },
      openingMessage: {
        required: required
      },
      selectedDebtNrs: {
        required: required,
        minLength: minLength(1)
      }
    },

    computed: {
      ...mapGetters({
        locale: 'locale',
        allDebts: 'allDebts',
        debtor: 'debtor'
      }),
      emailErrors: function () {
        var errors = []
        if (!this.$v.contactEmail.$dirty) return errors
        if (!this.$v.contactEmail.required) errors.push(this.$t('messenger.emailErrors.required'))
        if (!this.$v.contactEmail.email || !this.$v.contactEmail.strictEmail) errors.push(this.$t('messenger.emailErrors.email'))
        return errors
      },
      phoneErrors: function () {
        var errors = []
        if (!this.$v.contactPhoneNumber.$dirty) return errors
        if (!this.$v.contactPhoneNumber.numeric) errors.push(this.$t('messenger.phoneErrors.numeric'))
        if (!this.$v.contactPhoneNumber.minLength) errors.push(this.$t('messenger.phoneErrors.length'))
        if (this.$v.contactPhoneNumber.numeric && !this.$v.contactPhoneNumber.validFinnishPhone) {
          if (this.contactPhoneNumber && this.contactPhoneNumber.startsWith('0')) {
            errors.push(this.$t('messenger.phoneErrors.leadingZero'))
          } else {
            errors.push(this.$t('messenger.phoneErrors.length'))
          }
        }
        return errors
      },
      formattedDebts: function () {
        return this.allDebts.map(function (debt) {
          var ordernr = debt.orderNr || ''
          var creditorname = (debt.creditor && debt.creditor.name) || (debt.debtSummary && debt.debtSummary.creditor) || ''
          var amount = (debt.debtSummary && debt.debtSummary.capital) || 0
          return {
            text: ordernr + ' ' + creditorname + ' (' + amount + '€)',
            value: debt.nr
          }
        })
      },
      messageError: function () {
        var errors = []
        if (!this.$v.openingMessage.$dirty) return errors
        if (!this.$v.openingMessage.required) errors.push(this.$t('messenger.messageRequired'))
        return errors
      },
      debtsError: function () {
        var errors = []
        if (!this.$v.selectedDebtNrs.$dirty) return errors
        if (!this.$v.selectedDebtNrs.required || !this.$v.selectedDebtNrs.minLength) errors.push(this.$t('messenger.debtsRequired'))
        return errors
      },
      canSend: function () {
        return this.openingMessage.trim().length > 0 && this.selectedDebtNrs.length > 0
      }
    },

    watch: {
      value: function (val) {
        if (val) {
          this.reset()
          this.fetchTopics()
        }
      }
    },

    methods: {
      fetchTopics: function () {
        this.topicsLoading = true
        this.topicsError = false
        var self = this
        apiResource.get('api/messenger/topics').then(function (response) {
          self.topics = (response.data && response.data.responseObject) || response.data || []
          self.topicsLoading = false
        }).catch(function () {
          self.topicsError = true
          self.topicsLoading = false
        })
      },

      topicLabel: function (topic) {
        var locale = this.locale
        if (locale === 'se' && topic.sv) return topic.sv
        if (locale === 'en' && topic.en) return topic.en
        return topic.fi || topic.en || topic.sv || ''
      },

      normalizeEmail: function (value) {
        return (value || '').trim().toLowerCase()
      },

      normalizePhone: function (value) {
        return (value || '').trim()
      },

      persistContactInformation: async function () {
        var contactInfo = this.debtor && this.debtor.contactInformation
        var emailChanged = this.normalizeEmail(this.contactEmail) !== this.normalizeEmail(this.originalContactEmail)
        var phoneChanged = this.normalizePhone(this.contactPhoneNumber) !== this.normalizePhone(this.originalContactPhoneNumber)

        if (!emailChanged && !phoneChanged) return

        if (emailChanged) {
          var emailPayload = {
            ContactType: 'email',
            Text: this.contactEmail.trim(),
            Allow: true,
            Silent: phoneChanged
          }
          await this.$store.dispatch('addDebtorContact', emailPayload)
        }

        if (phoneChanged) {
          var phonePayload = {
            ContactType: 'phone',
            Text: this.contactPhoneNumber,
            Allow: true,
            Silent: emailChanged
          }
          await this.$store.dispatch('addDebtorContact', phonePayload)
        }

        if (emailChanged && phoneChanged) {
          this.$store.commit('setMessage', { type: 'primary', text: this.$t('messenger.contactInfoUpdated') })
        }

        this.originalContactEmail = this.contactEmail
        this.originalContactPhoneNumber = this.contactPhoneNumber
      },

      next: async function () {
        if (this.step === 2) {
          if (this.savingContact) return
          this.$v.contactEmail.$touch()
          this.$v.contactPhoneNumber.$touch()
          if (this.$v.contactEmail.$error || this.$v.contactPhoneNumber.$error || !this.contactConfirmed) return
          var contactChanged = this.normalizeEmail(this.contactEmail) !== this.normalizeEmail(this.originalContactEmail) ||
                               this.normalizePhone(this.contactPhoneNumber) !== this.normalizePhone(this.originalContactPhoneNumber)
          this.savingContact = true
          try {
            await this.persistContactInformation()
            if (contactChanged || !this.contactAllowPosted) {
              await apiResource.post('api/debtor/allow/email/true')
              await apiResource.post('api/debtor/allow/phone/true')
              this.contactAllowPosted = true
            }
          } catch (err) {
            this.savingContact = false
            return
          }
          this.savingContact = false
        }
        if (this.step < 3) this.step++
      },

      back: function () {
        if (this.step > 1) this.step--
      },

      send: function () {
        this.$v.openingMessage.$touch()
        this.$v.selectedDebtNrs.$touch()
        if (!this.canSend || this.starting) return
        var selectedTopic = this.topics.find(function (t) { return t.nr === this.selectedTopicNr }, this)
        this.starting = true
        this.$emit('start', {
          topicNr: this.selectedTopicNr,
          topicLabel: selectedTopic ? this.topicLabel(selectedTopic) : '',
          debtNrs: this.selectedDebtNrs,
          message: this.openingMessage.trim()
        })
      },

      reset: function () {
        this.step = 1
        this.topics = []
        this.topicsLoading = false
        this.topicsError = false
        this.selectedTopicNr = null
        var contactInfo = this.debtor && this.debtor.contactInformation
        var emails = (contactInfo && contactInfo.emails) || []
        this.contactEmail = (emails.length > 0 && emails[0].emailAddress) || ''
        var phone = (contactInfo && contactInfo.phones && contactInfo.phones[0] && contactInfo.phones[0].number) || ''
        if (phone.startsWith('+358')) {
          phone = phone.substring(4)
        } else if (phone.startsWith('0')) {
          phone = phone.substring(1)
        }
        this.contactPhoneNumber = phone
        this.originalContactEmail = this.contactEmail
        this.originalContactPhoneNumber = this.contactPhoneNumber
        this.contactConfirmed = false
        this.selectedDebtNrs = []
        this.openingMessage = ''
        this.starting = false
        this.savingContact = false
        this.contactAllowPosted = false
        this.$v && this.$v.$reset()
      },

      close: function () {
        this.$emit('input', false)
      }
    }
  }
</script>

<style scoped>
  .topic-list__inner {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: fit-content;
  }

  .topic-option {
    display: flex;
    align-items: center;
    padding: 6px 4px;
    cursor: pointer;
    font-size: 1rem;
    background: transparent;
    width: 100%;
  }

  .topic-option:hover {
    opacity: 0.8;
  }

  .topic-option--selected .topic-option__label {
    font-weight: bold;
  }

  .topic-option__label {
    flex: 1;
    text-align: left;
  }

  .topic-option__check {
    margin-right: 10px;
    flex-shrink: 0;
  }
</style>
